type FeatureType = 'boolean' | 'integer' | 'float' | 'string' | 'enumeration' | 'mixed';

export interface Feature<V = any> {
    Code: string;
    Type: FeatureType;
    DefaultValue: V;
    Value: V;
    Options?: string[];
    Minimum: number;
    Maximum: number;
    Global: boolean;
    Writable: boolean;
    ExpirationTime: number;
    UpdateTime: number;
}

export enum FeatureCode {
    /*
     * In the past whether you have a certain early-access (e.g. alpha / beta)
     * enabled or not was purely based on a client-side cookie and this feature's
     * value was to indicate not what your env was but rather what options were
     * available to you (whether you had access to both alpha & beta or only to beta).
     *
     * However, now we are persisting the users preference on the backed on their
     * settings under the same name "EarlyAccess" (either 0 or 1).
     *
     * To avoid confusing these two this has been renamed to "EarlyAccessScope".
     * Ideally we would rename it to that on the server as well (and when that happens
     * we can remove this comment :)).
     */
    AccountSecurityDismissed2FACard = 'AccountSecurityDismissed2FACard',
    AliasPromotion = 'AliasPromotion',
    AlmostAllMail = 'AlmostAllMail',
    AutoAddHolidaysCalendars = 'AutoAddHolidaysCalendars',
    AutoDelete = 'AutoDelete',
    AutoDowngradeReminder = 'AutoDowngradeReminder',
    BlockSenderInToolbar = 'BlockSenderInToolbar',
    BundlePromoShown = 'BundlePromoShown',
    CalendarEncryptedSearch = 'CalendarEncryptedSearch',
    CalendarEventColorSpotlight = 'CalendarEventColorSpotlight',
    CalendarVideoConferenceSpotlight = 'CalendarVideoConferenceSpotlight',
    CalendarRsvpNoteSpotlight = 'CalendarRsvpNoteSpotlight',
    ComposerAssistantSpotlight = 'ComposerAssistantSpotlight',
    ComposerAssistantInitialSetup = 'ComposerAssistantInitialSetup',
    ComposerAssistantTrialStartDate = 'ComposerAssistantTrialStartDate',
    DismissedRecoverDataCard = 'DismissedRecoverDataCard',
    DriveSearchSpotlight = 'DriveSearchSpotlight',
    DriveEasySwitchSidebarDismissed = 'DriveEasySwitchSidebarDismissed',
    DriveEasySwitchSpotlight = 'DriveEasySwitchSpotlight',
    EarlyAccessScope = 'EarlyAccess',
    ElectronESInboxThreshold = 'ElectronESInboxThreshold',
    ElectronConvPreloadAmount = 'ElectronConvPreloadAmount',
    InboxDesktopFreeTrialDates = 'InboxDesktopFreeTrialDates',
    InboxDesktopFreeTrialReminders = 'InboxDesktopFreeTrialReminders',
    MailActionsChunkSize = 'MailActionsChunkSize',
    NumAttachmentsWithoutEmbedded = 'NumAttachmentsWithoutEmbedded',
    NumberOfPreloadedConversations = 'NumberOfPreloadedConversations',
    PassNudgeDontShowAgain = 'PassNudgeDontShowAgain',
    ShowReferralTrialEndedBanner = 'ShowReferralTrialEndedBanner',
    ShowReferralTrialWillEndBanner = 'ShowReferralTrialWillEndBanner',

    // Flags needed by the one-dollar post-signup promo
    SubscriptionLastReminderDate = 'SubscriptionLastReminderDate',
    MailPostSignupOneDollarState = 'MailPostSignupOneDollarState',
    DrivePostSignupOneDollarState = 'DrivePostSignupOneDollarState',

    // Flags needed by the 0.99 promo for existing free users.
    MailPostSignupZeroNinetyNineState = 'MailPostSignupZeroNinetyNineState',
    HideMailPostSignupZeroNinetyNineOffer = 'HideMailPostSignupZeroNinetyNineOffer',

    // Flags needed by the b2b onboarding
    B2BOnboardingSpotlight = 'B2BOnboardingSpotlight',
    ShowB2BOnboardingButton = 'ShowB2BOnboardingButton',

    // Flags needed by the monthly subscribers nudge
    MailPaidUserNudgeTimestamp = 'MailPaidUserNudgeTimestamp',
    DrivePaidUserNudgeTimestamp = 'DrivePaidUserNudgeTimestamp',
    BundlePaidUserNudgeTimestamp = 'BundlePaidUserNudgeTimestamp',

    // Flags for the newsletter subscription view
    NewsletterSubscriptionViewOnboarding = 'NewsletterSubscriptionViewOnboarding',
    NewsletterSubscriptionSpotlight = 'NewsletterSubscriptionSpotlight',

    // Flags for the category views
    CategoryViewB2COnboardingViewFlags = 'CategoryViewB2COnboardingViewFlags',
    CategoryViewB2BOnboardingViewFlags = 'CategoryViewB2BOnboardingViewFlags',
    CategoryViewOnboardingAccountDateThreshold = 'CategoryViewOnboardingAccountDateThreshold',
    SpotlightCategoriesImproved = 'SpotlightCategoriesImproved',

    OfferPassFamilyPlan2024Yearly = 'OfferPassFamilyPlan2024Yearly',
    OfferGoUnlimited2022 = 'OfferGoUnlimited2022',
    OfferMailTrial2023 = 'OfferMailTrial2023',

    // Unlimited to Duo discounted pre-renewal offer
    OfferUnlimitedToDuoDiscounted = 'OfferUnlimitedToDuoDiscounted',

    // Bookings spotlights
    SpotlightIntroduceBookings = 'SpotlightIntroduceBookings',
    BookingPageLocationSpotlight = 'BookingPageLocationSpotlight',
    BookingSpotlightInMail = 'BookingSpotlightInMail',

    Offers = 'Offers',
    PrivacyDropdownOpened = 'PrivacyDropdownOpened',
    ProtonBadge = 'ProtonBadge',
    SpotlightVPNDrawer = 'SpotlightVPNDrawer',
    ReferralExplanationOpened = 'ReferralExplanationOpened',
    ReferralProgram = 'ReferralProgram', // Legacy referral program
    ReferralProgramSpotlight = 'ReferralProgramSpotlight', // Legacy referral program
    ReferralSpotlightSettings = 'ReferralSpotlightSettings',
    ReferralTopBarButton = 'ReferralTopBarButton',
    ReferralDrawerApp = 'ReferralDrawerApp',
    ScheduledSendFreemium = 'ScheduledSendFreemium',
    SetExpiration = 'SetExpiration',
    SpotlightBreachAlertSecurityCenter = 'SpotlightBreachAlertSecurityCenter',
    SpotlightLoadContent = 'SpotlightLoadContent',
    SpotlightScheduledSend = 'SpotlightScheduledSend',
    SpyTrackerProtectionIncorporator = 'SpyTrackerProtectionIncorporator',
    UsedContactsImport = 'UsedContactsImport',
    PassOnboardingSpotlights = 'PassOnboardingSpotlights',
    ProtonTipsSnoozeTime = 'ProtonTipsSnoozeTime',
    SeenLightLabellingFeatureModal = 'SeenLightLabellingFeatureModal',
    PostSubscriptionShortDomainSpotlight = 'PostSubscriptionShortDomainSpotlight',
    FeatureTourExpirationDate = 'FeatureTourExpirationDate',
    FeatureTourDrawerSpotlightDisplayDate = 'FeatureTourDrawerSpotlightDisplayDate',

    // Go unlimited 2025
    OfferGoUnlimited2025 = 'OfferGoUnlimited2025',
    OfferUnlimitedRotationState = 'OfferUnlimitedRotationState',

    // Unlimited to Duo permanent offer
    HideUnlimitedToDuoPermanentOffer = 'HideUnlimitedToDuoPermanentOffer',
    UnlimitedToDuoRotationState = 'UnlimitedToDuoRotationState',

    // Meet
    NewScheduleOptionSpotlight = 'NewScheduleOptionSpotlight',

    // BYOE
    BYOESpotlightModal = 'BYOESpotlightModal',

    // Lumo
    LumoSmoothedRendering = 'LumoSmoothedRendering',

    // Admin Roles
    AdminRolesOnboardingModal = 'AdminRolesOnboardingModal',
    AdminRolesTableSpotlight = 'AdminRolesTableSpotlight',
    AdminRolesPermissionsTabSpotlight = 'AdminRolesPermissionsTabSpotlight',
    AdminRolesGroupOnboardingModal = 'AdminRolesGroupOnboardingModal',
    AdminRolesGroupEditSpotlight = 'AdminRolesGroupEditSpotlight',

    // SCIM
    ScimGroupsOnboardingModal = 'ScimGroupsOnboardingModal',

    // NPS
    NPSFeedbackDesktopCalendar = 'NPSFeedbackDesktopCalendar',
    NPSFeedbackDesktopMail = 'NPSFeedbackDesktopMail',
    NPSFeedbackWebCalendar = 'NPSFeedbackWebCalendar',
    NPSFeedbackWebDrive = 'NPSFeedbackWebDrive',
    NPSFeedbackWebMail = 'NPSFeedbackWebMail',

    // Feedback First Cancellation Flow
    CanUseFeedbackFirstCancellationOffer = 'CanUseFeedbackFirstCancellationOffer',
}
